import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { Col, Spinner2 } from 'components';
import { Api, theme } from 'lib';
import ReferralAuthGate from './ReferralAuthGate';
import ReferralHeader from './ReferralHeader';
import ReferralInviteCard from './ReferralInviteCard';
import ReferralBanner from './ReferralBanner';
import ReferralFeed from './ReferralFeed';
import ReferralAwardModal from './ReferralAwardModal';
import ReferralErrorState from './ReferralErrorState';
import { REFERRAL_COLORS } from './theme';

const SELF_REFERRAL_ERR = 'Cannot refer yourself';

const PageBackground = styled.div`
  background-color: ${REFERRAL_COLORS.bg};
  min-height: 100vh;

  @media (min-width: 1024px) {
    height: calc(100vh - 74px);
    min-height: 0;
    overflow: hidden;
  }
`;

const Layout = styled.div`
  display: flex;
  flex-direction: column;

  @media (min-width: 1024px) {
    display: grid;
    grid-template-columns: minmax(380px, 460px) 1fr;
    grid-template-rows: auto auto auto 1fr;
    grid-template-areas:
      'stage header'
      'stage banner'
      'stage invite'
      'stage .';
    align-items: start;
    max-width: 1120px;
    margin: 0 auto;
    gap: 0 32px;
    padding: 32px 24px;
    height: 100%;
  }
`;

const GridArea = styled.div`
  grid-area: ${(props) => props.$area};
  ${(props) =>
    props.$area === 'stage' &&
    `
      display: flex;
      flex-direction: column;
      flex: 1;
      min-height: 0;
    `}
`;

/**
 * `fancamId` is set by a "View My FanCam Post" link: the referral then asks the friend to view
 * that one post of the sender's (the backend counts it alone, with a threshold of 1), and the
 * feed opens on it.
 */
const ReferralFlow = ({ senderId, username, shareToken, fancamId, origin }) => {
  const { t } = useTranslation();
  const currentUser = useSelector((state) => state.user);
  const { isMobile } = useSelector((state) => state.appState);
  const isLoggedIn = currentUser !== null;

  const [team, setTeam] = useState(null);
  const [sender, setSender] = useState(null);
  const [progress, setProgress] = useState(null);
  const [showAward, setShowAward] = useState(false);
  const [startedForTeam, setStartedForTeam] = useState(null);
  const [error, setError] = useState(null);

  const [arrivedCompleted, setArrivedCompleted] = useState(false);

  useEffect(() => {
    Api.get(`/users/getByUsername/${username}`).then((res) => {
      if (res) setTeam(res);
    });
  }, [username]);

  const startReferral = useCallback(
    async (teamId) => {
      const resolvedTeamId = teamId || team?._id;
      if (!resolvedTeamId) return;
      setError(null);
      // { silent: true } stops Api.post's default global-toast-on-error path
      // and instead resolves to { err, status } so the failure (e.g. self-
      // referral) can be rendered inline instead of leaving the screen stuck
      // on the loading spinner forever.
      const [progressRes, senderRes] = await Promise.all([
        Api.post(
          '/referral/start',
          {
            senderId,
            teamId: resolvedTeamId,
            shareToken,
            ...(fancamId ? { fancamId } : {}),
            ...(origin ? { origin } : {})
          },
          null,
          { silent: true }
        ),
        Api.get(`/users/getById/${senderId}`)
      ]);
      // A silent 5xx resolves to `{ status }` with no `err`, so test for the payload itself —
      // otherwise the banner renders "Earn undefined points" / "NaN Left" over a failed start.
      if (!progressRes || progressRes.err || typeof progressRes.momentsViewed !== 'number') {
        // Self-referral is a deliberate rule, not a glitch — no retry offered
        // for it. Everything else (network hiccup, 5xx) gets a retry, since
        // re-running the same request is a real fix for those.
        setError(
          progressRes?.err === SELF_REFERRAL_ERR
            ? {
                icon: '🤝',
                title: t('referral.selfReferralTitle'),
                body: t('referral.selfReferralBody')
              }
            : {
                icon: '⚠️',
                title: t('referral.startFailedTitle'),
                body: progressRes?.err || t('referral.startFailedBody'),
                canRetry: true
              }
        );
        return;
      }
      setProgress(progressRes);
      if (progressRes.status === 'completed') setArrivedCompleted(true);
      // getById responds as {user, subscription}, not the flat user object.
      if (senderRes?.user) setSender(senderRes.user);
      setStartedForTeam(resolvedTeamId);
    },
    [senderId, shareToken, fancamId, origin, team]
  );

  useEffect(() => {
    if (isLoggedIn && team?._id && startedForTeam !== team._id) {
      startReferral(team._id);
    }
  }, [isLoggedIn, team, startedForTeam, startReferral]);

  function handleViewTracked(result) {
    setProgress((prev) => ({
      ...prev,
      momentsViewed: result.momentsViewed,
      status: result.milestoneHit ? 'completed' : prev.status
    }));
    if (result.milestoneHit) setShowAward(true);
  }

  const loadingSpinner = (
    <Col
      wid="100%"
      ht="calc(100vh - 74px)"
      noFlex
      centerAll
      style={{ backgroundColor: REFERRAL_COLORS.bg }}
    >
      <Spinner2 col={theme.SECONDARY} />
    </Col>
  );

  if (!team) return loadingSpinner;
  if (!isLoggedIn)
    return <ReferralAuthGate team={team} onAuthenticated={() => startReferral(team._id)} />;
  if (error) {
    return (
      <ReferralErrorState
        icon={error.icon}
        title={error.title}
        body={error.body}
        onRetry={error.canRetry ? () => startReferral(team._id) : null}
      />
    );
  }
  if (!progress) return loadingSpinner;
  if (arrivedCompleted) {
    return (
      <ReferralErrorState
        icon="🎉"
        title={t('referral.alreadyCompletedTitle')}
        body={t('referral.alreadyCompletedBody', {
          friendName: sender?.name || sender?.username || t('referral.yourFriend'),
          points: progress.bonusPoints
        })}
      />
    );
  }

  return (
    <PageBackground>
      <Layout>
        <GridArea $area="header">
          <ReferralHeader team={team} />
        </GridArea>
        <GridArea $area="banner">
          <ReferralBanner
            senderName={sender?.name || sender?.username || 'a friend'}
            momentsViewed={progress.momentsViewed}
            threshold={progress.threshold}
            bonusPoints={progress.bonusPoints}
          />
        </GridArea>
        {(progress.momentsViewed <= 0 || !isMobile) && (
          <GridArea $area="invite">
            <ReferralInviteCard />
          </GridArea>
        )}
        <GridArea $area="stage">
          <ReferralFeed
            teamId={team._id}
            senderId={senderId}
            shareToken={shareToken}
            pinnedFancamId={fancamId}
            onViewTracked={handleViewTracked}
          />
        </GridArea>
        {showAward && (
          <ReferralAwardModal
            bonusPoints={progress.bonusPoints}
            momentsViewed={progress.momentsViewed}
            onContinue={() => setShowAward(false)}
          />
        )}
      </Layout>
    </PageBackground>
  );
};

export default ReferralFlow;
